import { useState, useEffect, useImperativeHandle, forwardRef, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FaLink, FaRegEye, FaUsers } from "react-icons/fa";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import axios from "axios";
import { toast } from "react-toastify";
import { MdInventory } from "react-icons/md";
import { FaPlus } from "react-icons/fa6";
import { BsThreeDotsVertical } from "react-icons/bs";
import SearchInput from "../../../utils/SearchInput";
import TableLoader from "../../../utils/TableLoader";
import PaginationComponent from "../../../utils/PaginationComponent";
import { TbEdit } from "react-icons/tb";
import { RiRestaurantFill, RiRefreshLine, RiArrowDownSLine, RiInformationLine } from "react-icons/ri";
import { BiSolidTrashAlt } from "react-icons/bi";
import RestaurantView from "./RestaurantView";
import RestaurantEdit from "./RestaurantEdit";
import ConfirmationModalReMatch from "./ConfirmationModalReMatch";

const ZOHO_STATUS_OPTIONS = [
  {
    value: "all",
    label: "Tutti",
    description: "Mostra tutti i ristoranti",
  },
  {
    value: "pending",
    label: "Pending",
    description:
      "Sono state rilevate nuove modifiche nel gestionale. Il ristorante verrà sincronizzato con Zoho non appena possibile.",
    badgeClass: "bg-amber-50 text-amber-800 border-amber-200",
  },
  {
    value: "processing",
    label: "Processing",
    description:
      "La sincronizzazione del ristorante con Zoho è attualmente in corso.",
    badgeClass: "bg-blue-50 text-blue-800 border-blue-200",
  },
  {
    value: "synced",
    label: "Synced",
    description:
      "Il ristorante è stato sincronizzato correttamente con Zoho.",
    badgeClass: "bg-emerald-50 text-emerald-800 border-emerald-200",
  },
  {
    value: "failed",
    label: "Failed",
    description:
      "Non è stato possibile completare la sincronizzazione con Zoho. Verifica i dati del ristorante e riprova oppure contatta l’assistenza.",
    badgeClass: "bg-rose-50 text-rose-800 border-rose-200 font-semibold",
  },
];

const RestaurantManagement = (props, ref) => {
  const { hasPermission } = props;

  // Navigate
  const navigate = useNavigate();

  const apiUrl = import.meta.env.VITE_METERI_API_BASE_URL;
  const abortControllerRef = useRef(null);

  // State Management: Data & Pagination
  const [restroData, setRestroData] = useState([]);
  const [isLoading, setIsLoading] = useState({
    main: false,
    edit: false,
    rematch: false,
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // State Management: Edit/View Mode
  const [currentRestroData, setCurrentRestroData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [userName, setUserName] = useState("");
  const [surName, setSurName] = useState("");
  const [restaurantName, setRestaurantName] = useState("");
  const [email, setEmail] = useState("");
  const [collaborationDescription, setCollaborationDescription] = useState("");
  const [mobilePhone, setMobilePhone] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [logo, setLogo] = useState(null);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [logoError, setLogoError] = useState("");
  const [isRematchModalOpen, setIsRematchModalOpen] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const logoUrl = import.meta.env.VITE_BACKEND_APP_STORAGE_URL;

  const [searchText, setSearchText] = useState("");
  const [showZohoFilter, setShowZohoFilter] = useState(false);
  const [zohoFilter, setZohoFilter] = useState("all");
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);

  const handleSearchChange = (value) => {
    setSearchText(value);
  };

  const changeRestaurantStatus = async (restID, currentStatus) => {
    setIsLoading((prev) => ({ ...prev, main: true }));
    try {
      const newStatus = currentStatus === "Active" ? "Inactive" : "Active";
      const response = await axios.put(
        `${apiUrl}/api/v1/restaurants/${restID}/status`,
        { status: newStatus },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (response.status === 200) {
        getRestroData();
      } else {
        console.error("Failed to update status:", response.statusText);
      }
    } catch (error) {
      console.error("Error updating restaurant status:", error);
    } finally {
      setIsLoading((prev) => ({ ...prev, main: false }));
    }
  };

  // Getting Restaurants Data From API
  const getRestroData = useCallback(async () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading((prev) => ({ ...prev, main: true }));
    try {
      const params = { pageNumber: page, size: rowsPerPage };
      if (searchText) params.search = searchText;
      if (zohoFilter && zohoFilter !== "all") params.sync_status = zohoFilter;

      const response = await axios.get(`${apiUrl}/api/v1/restaurants`, {
        params,
        signal: controller.signal,
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const data = response?.data || {};
      setRestroData(data?.data || []);
      setTotalPages(data?.total || 0);
    } catch (error) {
      if (!axios.isCancel(error)) console.error("Error fetching restaurants:", error);
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading((prev) => ({ ...prev, main: false }));
      }
    }
  }, [page, rowsPerPage, searchText, zohoFilter, apiUrl]);

  // Edit Handlers
  const handleEdit = (restro) => {
    setIsEditing(true);
    setIsViewing(false);
    setCurrentRestroData(restro);
  };

  // View Handlers
  const handleView = (restro) => {
    setIsViewing(true);
    setIsEditing(false);
    setCurrentRestroData(restro);
  };

  // Cancel Edit/View Mode
  const handleCancel = () => {
    setIsEditing(false);
    setIsViewing(false);
    setCurrentRestroData(null);
  };

  // Save Restaurant Data after Editing
  const handleSave = async () => {
    // {
    //   username: userName,
    //   surname: surName,
    //   restaurant_name: restaurantName,
    //   collaboration_description: collaborationDescription,
    //   mobile_phone: mobilePhone,
    //   company_name: companyName,
    // },
    if (!logoError) {
      const formData = new FormData();

      formData.append("username", userName);
      formData.append("surname", surName);
      formData.append("restaurant_name", restaurantName);
      formData.append("collaboration_description", collaborationDescription);
      formData.append("mobilePhone", mobilePhone);
      formData.append("companyName", companyName);
      formData.append("_method", "put");

      if (selectedLogo) {
        formData.append("logo", selectedLogo);
      }
      try {
        setIsLoading((prev) => ({ ...prev, edit: true }));
        const response = await axios.post(
          `${apiUrl}/api/v1/restaurants/${currentRestroData?.id}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        toast.success(response?.message || "Restaurant updated successfully!", {
          theme: "colored",
          autoClose: 1000,
        });
        setIsEditing(false);
        getRestroData();
      } catch (error) {
        console.error("Error Editing user:", error?.response?.data?.errors);
        const errors = error?.response?.data?.errors;
        if (errors) {
          for (const key in errors) {
            if (Array.isArray(errors[key])) {
              errors[key].forEach((message) => {
                toast.error(message, {
                  theme: "colored",
                  autoClose: 3000,
                });
              });
            }
          }
        } else {
          toast.error("Something went wrong. Please try again.", {
            theme: "colored",
            autoClose: 3000,
          });
        }
      } finally {
        setIsLoading((prev) => ({ ...prev, edit: false }));
      }
    } else {
      toast.error(logoError, { theme: "colored", autoClose: 1000 });
    }
  };

  // Delete Restaurant Data
  // const handleDelete =()=>{

  // }

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setLogoError("No file selected");
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      setLogoError("");
      setLogo((prev) => {
        if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
        return objectUrl;
      });
      setSelectedLogo(file);
    };
    img.onerror = () => {
      setLogoError("Invalid image file");
      URL.revokeObjectURL(objectUrl);
    };
    img.src = objectUrl;
  };

  useImperativeHandle(ref, () => ({
    restaurant() {
      setIsEditing(false);
      setIsViewing(false);
      setCurrentRestroData(null);
    },
  }));

  useEffect(() => {
    if (currentRestroData) {
      setUserName(currentRestroData?.username);
      setSurName(currentRestroData?.surname);
      setRestaurantName(currentRestroData?.restaurant_name);
      setEmail(currentRestroData?.email);
      setCollaborationDescription(currentRestroData?.collaboration_description);
      setMobilePhone(currentRestroData?.mobile_phone);
      setCompanyName(currentRestroData?.company_name);
      setLogo(
        currentRestroData?.logo
          ? `${logoUrl}/${currentRestroData?.logo}`
          : null,
      );
    }
  }, [currentRestroData]);

  useEffect(() => {
    getRestroData();
  }, [getRestroData]);

  // Abort in-flight request on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!event.target.closest(".zoho-dropdown-container")) {
        setShowZohoFilter(false);
      }
      if (!event.target.closest(".zoho-info-container")) {
        setShowInfoTooltip(false);
      }
    };
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    const handlePageRefresh = () => {
      localStorage.removeItem("searchText");
    };

    window.addEventListener("beforeunload", handlePageRefresh);

    return () => {
      window.removeEventListener("beforeunload", handlePageRefresh);
    };
  }, []);

  const openRematchModal = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setIsRematchModalOpen(true);
  };

  const handleZohoRematch = async () => {
    if (!selectedRestaurant) return;
    setIsLoading((prev) => ({ ...prev, rematch: true }));

    try {
      const { data } = await axios.get(
        `${apiUrl}/api/v1/zoho-crm/sync-restaurants`,
        {
          params: {
            phase: "phase2",
            limit: 1,
            restaurant_code: selectedRestaurant.code,
          },
        },
      );

      const phase2 = data?.summary?.phase2;

      if (phase2?.matched > 0) {
        toast.success(
          "Success! The restaurant was successfully matched with ZOHO.",
          { theme: "colored", autoClose: 3000 },
        );

        await getRestroData();
      } else if (phase2?.no_match > 0) {
        toast.error("No match found in ZOHO. It remains unmatched.", {
          theme: "colored",
          autoClose: 3000,
        });
      } else if (phase2?.errors > 0) {
        toast.error(
          "An error occurred while communicating with ZOHO. Please try again.",
          { theme: "colored", autoClose: 3000 },
        );
      } else {
        toast.info("Zoho Re-Match completed.", {
          theme: "colored",
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        "An error occurred while communicating with ZOHO. Please try again.",
        { theme: "colored", autoClose: 3000 },
      );
    } finally {
      setIsRematchModalOpen(false);
      setSelectedRestaurant(null);
      setIsLoading((prev) => ({ ...prev, rematch: false }));
    }
  };

  return (
    <>
      {!isEditing && !isViewing ? (
        <section className="border-t border-[#D9D9D9] sm:mt-5 pt-8">
          <div className="flex sm:flex-row flex-col justify-between items-center lg:mb-4 mb-5">
            <h3 className="md:text-3xl self-start	 text-lg font-bold mb-2 sm:mb-0 poppins-medium">
              Lista Ristoranti
            </h3>
            <div className="flex sm:flex-row flex-col items-center gap-4 sm:w-fit w-full sm:mt-0 mt-3">
              <div className="flex items-center gap-2 w-full sm:w-auto justify-end sm:justify-start">
                <div
                  className="relative zoho-info-container shrink-0"
                  onMouseEnter={() => {
                    if (window.matchMedia("(pointer: fine)").matches) {
                      setShowInfoTooltip(true);
                      setShowZohoFilter(false);
                    }
                  }}
                  onMouseLeave={() => {
                    if (window.matchMedia("(pointer: fine)").matches) {
                      setShowInfoTooltip(false);
                    }
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setShowInfoTooltip(!showInfoTooltip);
                      setShowZohoFilter(false);
                    }}
                    className="text-[#8B2433] hover:text-[#A72B3C] transition-colors p-1 flex items-center justify-center rounded-full hover:bg-[#8b24330c]"
                  >
                    <RiInformationLine size="22px" />
                  </button>
                  {showInfoTooltip && (
                    <>
                      <style>{`
                        @keyframes tooltipFadeIn {
                          from {
                            opacity: 0;
                            transform: translateY(-8px) scale(0.95);
                          }
                          to {
                            opacity: 1;
                            transform: translateY(0) scale(1);
                          }
                        }
                      `}</style>
                      <div
                        style={{ animation: "tooltipFadeIn 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards" }}
                        className="absolute top-full mt-3 left-[-10px] right-auto w-72 sm:w-80 md:left-[-150px] md:w-[600px] bg-white/95 backdrop-blur-md border border-[#F2C3CA] rounded-2xl shadow-2xl p-5 text-[#303030] text-xs z-50 poppins-regular"
                      >
                        <div className="flex items-center gap-2 border-b border-[#F2C3CA] pb-2.5 mb-3.5">
                          <span className="w-2 h-2 rounded-full bg-[#8B2433] animate-pulse"></span>
                          <h4 className="font-semibold text-xs text-[#8B2433] poppins-medium">
                            Guida Stati di Sincronizzazione Zoho
                          </h4>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3.5">
                          {ZOHO_STATUS_OPTIONS.filter(opt => opt.value !== "all").map((item) => (
                            <div key={item.value} className="flex flex-col gap-1 border-b border-gray-50 md:border-b-0 pb-3 md:pb-0">
                              <span className={`self-start px-2 py-0.5 text-[9px] font-medium tracking-wide uppercase rounded border ${item.badgeClass}`}>
                                {item.label}
                              </span>
                              <p className="text-[11px] leading-relaxed text-gray-500 poppins-regular">
                                {item.description}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <div className="relative text-sm bg-transparent zoho-dropdown-container flex-1 sm:flex-initial sm:w-44 w-full">
                  <button
                    className={`${showZohoFilter
                        ? "border-b-transparent rounded-tl-[24px] rounded-tr-[24px]"
                        : "rounded-full"
                      } py-[7px] border bg-[#fff] border-[#F2C3CA] inline-flex justify-between items-center px-4 outline-none text-[#8B2433] font-medium shadow-sm relative z-30 w-full`}
                    onClick={() => {
                      setShowZohoFilter(!showZohoFilter);
                      setShowInfoTooltip(false);
                    }}
                  >
                    <span className="mr-2 flex items-center gap-3 poppins-medium text-sm text-[#8B2433]">
                      {zohoFilter === "all"
                        ? "Stato Zoho"
                        : ZOHO_STATUS_OPTIONS.find((opt) => opt.value === zohoFilter)?.label || "Stato Zoho"}
                    </span>
                    <RiArrowDownSLine
                      className={`transform transition-transform duration-300 ${showZohoFilter ? "rotate-180" : ""
                        }`}
                      size={"20px"}
                      color="#8B2433"
                    />
                  </button>
                  <div
                    className={`
                      ${showZohoFilter
                        ? "block z-50"
                        : "hidden z-20"
                      }
                      bg-[#fff] overflow-hidden absolute pt-[9px] rounded-t-none 
                      border-t-0 p-2 px-4 right-0 top-full -mt-[1px] rounded-b-[24px] shadow-lg text-[#303030] space-y-1 
                      w-full border border-[#F2C3CA]
                    `}
                  >
                    {ZOHO_STATUS_OPTIONS.map((option, idx, arr) => (
                      <label
                        key={option.value}
                        className={`${idx !== arr.length - 1 ? "border-b border-[#8b24331f]" : ""
                          } px-0 py-2 flex align-center justify-between flex-row-reverse gap-[15px] cursor-pointer`}
                      >
                        <input
                          type="checkbox"
                          checked={zohoFilter === option.value}
                          onChange={() => {
                            setZohoFilter(option.value);
                            setShowZohoFilter(false);
                            setPage(1);
                          }}
                          className="accent-[#8B2433] h-4 w-4 outline-none cursor-pointer"
                        />
                        <span className="text-[#303030] rounded-md flex items-center gap-3 poppins-regular text-xs">
                          {option.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <SearchInput
                onSearchChange={handleSearchChange}
                component="Restaurant Management"
                placeholder="Ristoranti"
              />
            </div>
          </div>
          <div className="shadow-scroll-x overflow-x-scroll relative user-mangement-main h-auto bg-[#FBFBFB] border-[2px] border-[#8B24332B] px-6 pt-3 pb-0 rounded-xl shadow">
            <div className="">
              <table className="lg:w-full md:w-[600px] text-left wine_table">
                <thead>
                  <tr className="border-b border-[#8B2433]">
                    <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">
                      Codice
                    </th>
                    <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">
                      Nome Utente
                    </th>
                    <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">
                      Nome Ristorante
                    </th>
                    <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">
                      Ragione Sociale
                    </th>
                    <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B] w-[150px]">
                      Cellulare
                    </th>
                    <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">
                      Email
                    </th>
                    <th className="pb-3 px-2 text-left poppins-medium text-[#8A2533]">
                      Zoho sync status
                    </th>
                    {hasPermission("Restaurants", "edit") && (
                      <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">
                        Stato
                      </th>
                    )}
                    <th className="pb-3 px-7 text-left poppins-medium text-[#6B6B6B] w-[150px]">
                      Opzioni
                    </th>
                  </tr>
                </thead>
                {isLoading?.main ? (
                  <TableLoader />
                ) : (
                  <tbody>
                    {restroData?.length ? (
                      restroData?.map((restaurant, index) => (
                        <tr
                          key={index}
                          className="border-b border-[#8B2433]/12"
                        >
                          <td className="py-4 px-2 text-left poppins-medium text-[#303030]">
                            {restaurant?.code ? restaurant?.code : "-"}
                          </td>
                          <td className="py-4 px-2 text-left poppins-medium text-[#303030]">
                            {restaurant?.username ? restaurant?.username : "-"}
                          </td>
                          <td className="py-4 px-2 text-left poppins-medium text-[#303030]">
                            {restaurant?.restaurant_name
                              ? restaurant?.restaurant_name
                              : "-"}
                          </td>
                          <td className="py-4 px-2 text-left poppins-medium text-[#303030]">
                            {restaurant?.company_name
                              ? restaurant?.company_name
                              : "-"}
                          </td>
                          <td className="py-4 px-2 text-left poppins-medium text-[#303030]">
                            {restaurant?.mobile_phone
                              ? restaurant?.mobile_phone
                              : "-"}
                          </td>
                          <td className="py-4 px-2 text-left poppins-medium text-[#303030]">
                            {restaurant?.email ? restaurant?.email : "-"}
                          </td>
                          <td className="py-4 px-2 text-left poppins-medium text-[#303030]">
                            <div className="flex items-center justify-start min-h-[20px]">
                              {(() => {
                                let statusKey = restaurant?.sync_status;
                                // Map removed statuses to their grouped parent statuses
                                if (statusKey === "updated") statusKey = "synced";
                                if (statusKey === "unmatched") statusKey = "failed";
                                
                                const statusOpt = ZOHO_STATUS_OPTIONS.find((opt) => opt.value === statusKey);
                                const isClickable = restaurant?.zoho_id && ["synced", "updated"].includes(restaurant?.sync_status);
                                
                                const badge = statusOpt ? (
                                  <span className={`inline-flex items-center justify-center px-2.5 py-[3px] text-[11px] font-medium tracking-wide uppercase rounded-md border ${isClickable ? "cursor-pointer" : "cursor-not-allowed"} ${statusOpt.badgeClass || ""}`}>
                                    {statusOpt.label}
                                  </span>
                                ) : (
                                  <span className={`text-[#303030] ${isClickable ? "cursor-pointer" : "cursor-not-allowed"}`}>-</span>
                                );

                                if (isClickable) {
                                  return (
                                    <a
                                      href={`${import.meta.env.VITE_ZOHO_CRM_URL}/${restaurant.zoho_id}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="hover:opacity-80 transition-opacity flex items-center"
                                    >
                                      {badge}
                                    </a>
                                  );
                                }

                                return badge;
                              })()}
                            </div>
                          </td>
                          {hasPermission("Restaurants", "edit") && (
                            <td className="py-4 px-2">
                              <div className="flex items-center justify-start min-h-[20px]">
                                <label className="inline-flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    value=""
                                    checked={
                                      restaurant?.status === "Active"
                                        ? true
                                        : false
                                    }
                                    onChange={() => {
                                      changeRestaurantStatus(
                                        restaurant?.id,
                                        restaurant?.status,
                                      );
                                    }}
                                    className="sr-only peer"
                                  />
                                  <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full after:absolute after:top-[2px] after:start-[2px] after:bg-white after:h-4 after:w-4 after:rounded-full after:transition-all peer-checked:bg-[#8B2433]"></div>
                                </label>
                              </div>
                            </td>
                          )}
                          <td className="py-4 px-7">
                            <div className="text-red-700 gap-4 flex items-center justify-start">
                              <button
                                onClick={() =>
                                  navigate(`/restaurant/${restaurant?.slug}`)
                                }
                              >
                                <FaLink color="#8B2433" size={"18px"} />
                              </button>
                              {hasPermission("WineList", "view") && (
                                <button
                                  onClick={() =>
                                    navigate(
                                      `/admin/restaurantManagement/${restaurant?.id}`,
                                    )
                                  }
                                >
                                  <FaPlus color="#8B2433" size={"22px"} />
                                </button>
                              )}
                              <Menu>
                                <MenuButton>
                                  <BsThreeDotsVertical
                                    color="#8B2433"
                                    size={"18px"}
                                  />
                                </MenuButton>
                                <MenuItems
                                  anchor="bottom"
                                  className="bg-[#fffbfb] w-[140px] border text-black py-0 flex flex-col  shadow-2xl rounded-xl relative focus-visible:outline-none overflow-hidden"
                                >
                                  {/* Arrow at the top */}
                                  <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-white"></div>

                                  {hasPermission("Restaurants", "view") && (
                                    <MenuItem className="flex gap-2 justify-start items-center poppins-regular px-3 pe-3 py-2 hover:bg-[#8b2532] hover:text-[#fff] transition-all duration-300 ease-in-out">
                                      <button
                                        onClick={() => handleView(restaurant)}
                                        className="w-full text-left flex gap-2 items-center whitespace-nowrap overflow-hidden text-ellipsis"
                                      >
                                        <FaRegEye
                                          size={"18px"}
                                          className="flex-shrink-0"
                                        />
                                        <span className="truncate">
                                          Visualizza
                                        </span>
                                      </button>
                                    </MenuItem>
                                  )}

                                  {hasPermission("Restaurants", "edit") && (
                                    <MenuItem className="flex gap-2 justify-start border-t items-center poppins-regular px-3 py-2 hover:bg-[#8b2532] hover:text-[#fff] transition-all duration-300 ease-in-out">
                                      <button
                                        onClick={() => handleEdit(restaurant)}
                                        className="w-full text-left flex gap-2 items-center whitespace-nowrap overflow-hidden text-ellipsis"
                                      >
                                        <TbEdit
                                          size={"18px"}
                                          className="flex-shrink-0"
                                        />
                                        <span className="truncate">
                                          Modifica
                                        </span>
                                      </button>
                                    </MenuItem>
                                  )}

                                  {hasPermission("WineStock", "view") && (
                                    <MenuItem className="flex gap-2 justify-start border-t items-center poppins-regular px-3 py-2 hover:bg-[#8b2532] hover:text-[#fff] transition-all duration-300 ease-in-out">
                                      <button
                                        onClick={() =>
                                          navigate(
                                            `/admin/wineStock/${restaurant?.id}`,
                                          )
                                        }
                                        className="w-full text-left flex gap-2 items-center whitespace-nowrap overflow-hidden text-ellipsis"
                                      >
                                        <MdInventory
                                          size={"18px"}
                                          className="flex-shrink-0"
                                        />
                                        <span className="truncate">
                                          Cantina
                                        </span>
                                      </button>
                                    </MenuItem>
                                  )}

                                  {["Super Admin", "Sales Person"].includes(
                                    localStorage.getItem("RoleName"),
                                  ) && (
                                      <MenuItem className="flex gap-2 justify-start border-t items-center poppins-regular px-3 py-2 hover:bg-[#8b2532] hover:text-[#fff] transition-all duration-300 ease-in-out">
                                        <button
                                          onClick={() => {
                                            localStorage.setItem(
                                              "currentRestId",
                                              restaurant?.id,
                                            );
                                            localStorage.setItem(
                                              "accessToAdmin",
                                              true,
                                            );
                                            navigate(
                                              `/restaurantPanel/dashboard/${restaurant?.id}`,
                                            );
                                          }}
                                          className="w-full text-left flex gap-2 items-center overflow-hidden text-ellipsis"
                                        >
                                          <RiRestaurantFill
                                            size={"18px"}
                                            className="flex-shrink-0"
                                          />
                                          <span>Console Ristorante</span>
                                        </button>
                                      </MenuItem>
                                    )}

                                  {hasPermission("Users", "view") && (
                                    <MenuItem className="flex gap-2 justify-start border-t items-center poppins-regular px-3 py-2 hover:bg-[#8b2532] hover:text-[#fff] transition-all duration-300 ease-in-out">
                                      <button
                                        onClick={() => {
                                          localStorage.setItem(
                                            "currentRestId",
                                            restaurant?.id,
                                          );
                                          localStorage.setItem(
                                            "accessToAdmin",
                                            true,
                                          );
                                          navigate(
                                            `/restaurantPanel/restaurant-user/${restaurant?.id}`,
                                          );
                                        }}
                                        className="w-full text-left flex gap-2 items-center whitespace-nowrap overflow-hidden text-ellipsis"
                                      >
                                        <FaUsers
                                          size={"18px"}
                                          className="flex-shrink-0"
                                        />
                                        <span className="truncate">Utenti</span>
                                      </button>
                                    </MenuItem>
                                  )}

                                  {hasPermission("Restaurants", "remove") && (
                                    <MenuItem className="flex gap-2 justify-start border-t items-center poppins-regular px-3 py-2 hover:bg-[#8b2532] hover:text-[#fff] transition-all duration-300 ease-in-out">
                                      <button className="w-full text-left flex gap-2 items-center whitespace-nowrap overflow-hidden text-ellipsis">
                                        <BiSolidTrashAlt
                                          size={"18px"}
                                          className="flex-shrink-0"
                                        />
                                        <span className="truncate">
                                          Elimina
                                        </span>
                                      </button>
                                    </MenuItem>
                                  )}
                                  <MenuItem className="flex gap-2 justify-start border-t items-center poppins-regular px-3 py-2 hover:bg-[#8b2532] hover:text-[#fff] transition-all duration-300 ease-in-out">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        openRematchModal(restaurant)
                                      }
                                      className="w-full text-left flex gap-2 items-center"
                                    >
                                      <RiRefreshLine size={18} />
                                      <span>Zoho Re-Match</span>
                                    </button>
                                  </MenuItem>
                                </MenuItems>
                              </Menu>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={9} className="text-center py-4">
                          Nessun ristorante trovato
                        </td>
                      </tr>
                    )}
                  </tbody>
                )}
              </table>
            </div>
          </div>
          {restroData && restroData?.length > 0 && (
            <PaginationComponent
              page={page}
              totalPages={totalPages}
              rowsPerPage={rowsPerPage}
              setPage={setPage}
              setRowsPerPage={setRowsPerPage}
            />
          )}
        </section>
      ) : isViewing ? (
        <RestaurantView
          currentRestroData={currentRestroData}
          handleCancel={handleCancel}
        />
      ) : (
        <RestaurantEdit
          isLoading={isLoading}
          userName={userName}
          surName={surName}
          restaurantName={restaurantName}
          email={email}
          collaborationDescription={collaborationDescription}
          companyName={companyName}
          mobilePhone={mobilePhone}
          logo={logo}
          logoError={logoError}
          setUserName={setUserName}
          setSurName={setSurName}
          setRestaurantName={setRestaurantName}
          setEmail={setEmail}
          setCollaborationDescription={setCollaborationDescription}
          setMobilePhone={setMobilePhone}
          setCompanyName={setCompanyName}
          handleLogoChange={handleLogoChange}
          handleView={handleView}
          handleSave={handleSave}
          handleCancel={handleCancel}
        />
      )}
      {isRematchModalOpen && (
        <ConfirmationModalReMatch
          isRematchModalOpen={isRematchModalOpen}
          setIsRematchModalOpen={setIsRematchModalOpen}
          setSelectedRestaurant={setSelectedRestaurant}
          handleZohoRematch={handleZohoRematch}
          loading={isLoading.rematch}
        />
      )}
    </>
  );
};

export default forwardRef(RestaurantManagement);